import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import { TextInput, Button } from "src/Components";
// Types
import { IMinProfit, SyntheticApiTab } from "src/Interfaces/Synthetic";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Requests
import {
  getPairType,
  getMinProfit,
  setMinProfit,
} from "src/Requests/synthetic";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";

interface IProps {
  apiTab?: SyntheticApiTab;
  onClose: () => void;
}

type DuedateMode = "same" | "different";

type DuedateGroup = {
  baseName: string;
  order: number;
  same?: DropdownItem;
  different?: DropdownItem;
};

const verticalSpreadIds = new Set(["13", "14", "15", "16"]);
const arbitrageIds = new Set(["1", "2", "5", "6"]);
const arbitrageDuedateIds = new Set(["31", "32", "41", "42"]);

const isVerticalSpread = (item: DropdownItem) =>
  verticalSpreadIds.has(item.id.toString());

const isArbitrage = (item: DropdownItem) => arbitrageIds.has(item.id.toString());

const isArbitrageDuedate = (item: DropdownItem) =>
  arbitrageDuedateIds.has(item.id.toString());

const getDuedateMode = (
  item: DropdownItem,
): { baseName: string; mode: DuedateMode } | null => {
  const sameSuffix = " - هم سر رسید";
  const differentSuffix = " - غیر هم سر رسید";

  if (item.name.endsWith(sameSuffix)) {
    return {
      baseName: item.name.slice(0, -sameSuffix.length),
      mode: "same",
    };
  }

  if (item.name.endsWith(differentSuffix)) {
    return {
      baseName: item.name.slice(0, -differentSuffix.length),
      mode: "different",
    };
  }

  return null;
};

const addDuedateGroup = (
  groups: Map<string, DuedateGroup>,
  item: DropdownItem,
  order: number,
  duedateMode: { baseName: string; mode: DuedateMode },
) => {
  const group = groups.get(duedateMode.baseName) ?? {
    baseName: duedateMode.baseName,
    order,
  };

  group[duedateMode.mode] = item;
  groups.set(duedateMode.baseName, group);
};

const ExpectedReturnModal: React.FC<IProps> = (props) => {
  // local store
  const [config, setConfig] = React.useState<IMinProfit>({});
  const [pairType, setPairType] = React.useState<DropdownItem[]>([]);
  React.useEffect(() => {
    Promise.all([getPairType(props.apiTab), getMinProfit(props.apiTab)])
      .then(([pairTypes, res]) => {
        const newConfig = Object.assign({}, ...res);
        const configKeys = new Set(Object.keys(newConfig));
        setConfig(newConfig);
        setPairType(
          pairTypes.filter((item) => configKeys.has(item.id.toString())),
        );
      })
      .catch(packetErrorHandler);
  }, [props.apiTab]);

  const changeHandler = (name: string, value: any) => {
    setConfig((prev) => ({
      ...prev,
      [name as `${number}`]: value ?? "",
    }));
  };

  const {
    otherPairTypes,
    otherDuedateGroups,
    arbitragePairTypes,
    arbitrageDuedateGroups,
    verticalPairTypes,
  } = React.useMemo(() => {
    const otherItems: DropdownItem[] = [];
    const arbitrageItems: DropdownItem[] = [];
    const verticalItems: DropdownItem[] = [];
    const otherGroups = new Map<string, DuedateGroup>();
    const arbitrageGroups = new Map<string, DuedateGroup>();

    pairType.forEach((item, index) => {
      if (isArbitrage(item)) {
        arbitrageItems.push(item);
        return;
      }

      if (isVerticalSpread(item)) {
        verticalItems.push(item);
        return;
      }

      const duedateMode = getDuedateMode(item);
      if (!duedateMode) {
        otherItems.push(item);
        return;
      }

      if (isArbitrageDuedate(item)) {
        addDuedateGroup(arbitrageGroups, item, index, duedateMode);
        return;
      }

      addDuedateGroup(otherGroups, item, index, duedateMode);
    });

    return {
      otherPairTypes: otherItems,
      otherDuedateGroups: Array.from(otherGroups.values()).sort(
        (first, second) => first.order - second.order,
      ),
      arbitragePairTypes: arbitrageItems,
      arbitrageDuedateGroups: Array.from(arbitrageGroups.values()).sort(
        (first, second) => first.order - second.order,
      ),
      verticalPairTypes: verticalItems,
    };
  }, [pairType]);

  const renderInput = (item: DropdownItem, label: string) => {
    const key = item.id.toString() as `${number}`;
    const value = config[key];

    return (
      <div key={key} className="flex w-full items-center gap-3">
        <span className="text-sm text-gray-500 w-64 shrink-0 text-right whitespace-nowrap">
          % {label}
        </span>
        <div className="w-[220px] shrink-0">
          <TextInput
            label=""
            name={item.id.toString()}
            onChange={changeHandler}
            value={value == null ? "" : String(value)}
            kind="negativeDecimalNumbers"
            extraLabel
          />
        </div>
      </div>
    );
  };

  const renderSection = (title: string, children: React.ReactNode) => (
    <div className="flex flex-col gap-3 w-[1030px] max-w-[88vw]">
      <div className="text-sm font-bold text-gray-700 text-right border-b border-gray-200 pb-2">
        {title}
      </div>
      {children}
    </div>
  );

  const renderDuedateGroups = (groups: DuedateGroup[]) => (
    <>
      {groups.map((group) => (
        <div
          key={group.baseName}
          className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3"
        >
          {group.same &&
            renderInput(group.same, `${group.baseName} - هم سر رسید`)}
          {group.different &&
            renderInput(group.different, `${group.baseName} - غیر هم سر رسید`)}
        </div>
      ))}
    </>
  );

  const setConfigHandler = () => {
    const newConfig = Object.entries(config).map(([key, value]) => ({
      [key]: value,
    }));
    setMinProfit(newConfig, props.apiTab)
      .then((resp) => {
        toast.success(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => {
        props.onClose();
      });
  };

  return (
    <div className="w-full flex flex-col justify-center items-center gap-3">
      {(arbitragePairTypes.length > 0 ||
        arbitrageDuedateGroups.length > 0) &&
        renderSection(
          "آربیتراژ",
          <div className="flex flex-col gap-3">
            {arbitragePairTypes.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                {arbitragePairTypes.map((item) =>
                  renderInput(item, item.name),
                )}
              </div>
            )}

            {renderDuedateGroups(arbitrageDuedateGroups)}
          </div>,
        )}

      {verticalPairTypes.length > 0 && (
        renderSection(
          "Vertical Spread",
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {verticalPairTypes.map((item) => renderInput(item, item.name))}
          </div>,
        )
      )}

      {(otherPairTypes.length > 0 || otherDuedateGroups.length > 0) &&
        renderSection(
          "سایر",
          <div className="flex flex-col gap-3">
            {otherPairTypes.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                {otherPairTypes.map((item) => renderInput(item, item.name))}
              </div>
            )}

            {renderDuedateGroups(otherDuedateGroups)}
          </div>,
        )}

      <div className="w-[200px] mt-7">
        <Button onClick={setConfigHandler} text="ثبت" variant="contained" />
      </div>
    </div>
  );
};

export default ExpectedReturnModal;
